import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Tooltip,
  Avatar
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { format } from 'date-fns';
import { ChatMessage } from '../../types/chat.types';

interface MessageItemProps {
  isContinuation: boolean;
  isOwn: boolean; // same sender within 5 min of previous
  message: ChatMessage;
  onDelete: (messageId: string) => void;
  onEdit: (messageId: string, newText: string) => void;
  showAvatar: boolean; // first message in a group
  showTimestamp: boolean; // last message in a group
  user?: any;
}

const getInitials = (user: any): string => {
  if (!user) return 'U';
  return (
    `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() ||
    'U'
  );
};

const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + (hash * 31 - hash);
  }
  return `hsl(${Math.abs(hash) % 360}, 65%, 50%)`;
};

// Compute bubble border radii without nested ternaries
const getBubbleRadius = (
  isOwn: boolean,
  isContinuation: boolean,
  showTimestamp: boolean
) => {
  const full = '18px';
  const small = '5px';
  if (isOwn) {
    const topRight = isContinuation ? small : full;
    let bottomRight = full;
    if (showTimestamp) bottomRight = small;
    else if (isContinuation) bottomRight = small;
    return {
      borderBottomLeftRadius: full,
      borderBottomRightRadius: bottomRight,
      borderTopLeftRadius: full,
      borderTopRightRadius: topRight
    };
  }
  const topLeft = isContinuation ? small : full;
  let bottomLeft = full;
  if (showTimestamp) bottomLeft = small;
  else if (isContinuation) bottomLeft = small;
  return {
    borderBottomLeftRadius: bottomLeft,
    borderBottomRightRadius: full,
    borderTopLeftRadius: topLeft,
    borderTopRightRadius: full
  };
};

function MessageItem({
  message,
  isOwn,
  isContinuation,
  showAvatar,
  showTimestamp,
  onEdit,
  onDelete,
  user
}: MessageItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(message.messageText);
  const [showActions, setShowActions] = useState(false);

  const handleEdit = () => {
    if (editText.trim() && editText !== message.messageText) {
      onEdit(message.id, editText.trim());
    }
    setIsEditing(false);
    setEditText(message.messageText);
  };

  const handleDelete = () => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Delete this message?')) {
      onDelete(message.id);
    }
  };

  const displayText = message.translatedText || message.messageText;
  const isTranslated = Boolean(
    message.translatedText && message.translatedText !== message.messageText
  );
  const initials = getInitials(user);
  const avatarColor = user
    ? stringToColor(`${user.firstName}${user.lastName}`)
    : '#9e9e9e';

  const br = getBubbleRadius(isOwn, isContinuation, showTimestamp);

  return (
    <Box
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      sx={{
        alignItems: 'flex-end',
        display: 'flex',
        flexDirection: isOwn ? 'row-reverse' : 'row',
        gap: '8px',
        mb: showTimestamp ? '6px' : '2px',
        mt: isContinuation ? 0 : '6px',
        px: 2
      }}
    >
      {/* Avatar column — always 28px wide to keep bubbles aligned */}
      <Box sx={{ flexShrink: 0, mb: '2px', width: 28 }}>
        {showAvatar && !isOwn && (
          <Avatar
            sx={{
              bgcolor: avatarColor,
              fontSize: '0.68rem',
              fontWeight: 700,
              height: 28,
              width: 28
            }}
          >
            {initials}
          </Avatar>
        )}
      </Box>

      {/* Bubble column */}
      <Box
        sx={{
          alignItems: isOwn ? 'flex-end' : 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '62%'
        }}
      >
        {/* Sender name — only on first bubble of received group */}
        {showAvatar && !isOwn && (
          <Typography
            sx={{
              color: 'text.disabled',
              fontSize: '0.68rem',
              fontWeight: 600,
              mb: '3px',
              pl: '4px'
            }}
          >
            {user ? `${user.firstName} ${user.lastName}` : 'Unknown'}
          </Typography>
        )}

        {isEditing ? (
          <Box sx={{ minWidth: 240 }}>
            <TextField
              autoFocus
              fullWidth
              multiline
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleEdit();
                }
                if (e.key === 'Escape') {
                  setIsEditing(false);
                  setEditText(message.messageText);
                }
              }}
              size="small"
              value={editText}
              variant="outlined"
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Button onClick={handleEdit} size="small" variant="contained">
                Save
              </Button>
              <Button
                onClick={() => {
                  setIsEditing(false);
                  setEditText(message.messageText);
                }}
                size="small"
                variant="outlined"
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ position: 'relative' }}>
            {/* Hover action buttons — float to the side, outside the bubble */}
            {isOwn && showActions && (
              <Box
                sx={{
                  alignItems: 'center',
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  bottom: 0,
                  boxShadow: '0 1px 6px rgba(0,0,0,0.10)',
                  display: 'flex',
                  mr: 0.75,
                  position: 'absolute',
                  px: 0.25,
                  right: '100%'
                }}
              >
                <Tooltip title="Edit">
                  <IconButton
                    onClick={() => setIsEditing(true)}
                    size="small"
                    sx={{ color: 'text.secondary' }}
                  >
                    <Edit sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    onClick={handleDelete}
                    size="small"
                    sx={{ color: 'error.main' }}
                  >
                    <Delete sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {/* The bubble itself */}
            <Box
              sx={{
                bgcolor: isOwn ? 'primary.main' : 'grey.100',
                px: 1.5,
                py: 0.875,
                ...br
              }}
            >
              <Typography
                sx={{
                  color: isOwn ? 'white' : 'text.primary',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {displayText}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Timestamp row — only on last message of each group */}
        {showTimestamp && (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'row',
              gap: 0.5,
              justifyContent: isOwn ? 'flex-end' : 'flex-start',
              mt: '3px',
              px: '4px'
            }}
          >
            <Typography sx={{ color: 'text.disabled', fontSize: '0.67rem' }}>
              {format(new Date(message.createdAt), 'h:mm a')}
            </Typography>
            {isTranslated && (
              <Typography
                sx={{
                  color: 'primary.light',
                  fontSize: '0.67rem',
                  fontWeight: 500
                }}
              >
                · Translated
              </Typography>
            )}
            {message.isEdited && (
              <Typography sx={{ color: 'text.disabled', fontSize: '0.67rem' }}>
                · Edited
              </Typography>
            )}
            {isOwn && (
              <Typography
                sx={{
                  color:
                    message.readReceipts && message.readReceipts.length > 0
                      ? 'primary.main'
                      : 'text.disabled',
                  fontSize: '0.72rem',
                  lineHeight: 1
                }}
              >
                {message.readReceipts && message.readReceipts.length > 0
                  ? '✓✓'
                  : '✓'}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

MessageItem.defaultProps = {
  user: undefined
};

export { MessageItem };
export default MessageItem;
